import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Megaphone, ArrowRight, ChevronRight } from 'lucide-react';
import { CATEGORY_STYLES, fmtDate } from './announcementUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCENT = '#E60000';

/**
 * AnnouncementPopup — login popup listing the user's UNSEEN announcements.
 * Matches the survey popup's two-panel layout/size for a consistent look.
 * Rendered only after the mandatory survey phase resolves (see App.js `active` prop).
 */
export default function AnnouncementPopup({ active }) {
  const { sessionToken, getAuthHeader, employee } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const onFillRoute = /^\/surveys\/[^/]+\/fill/.test(location.pathname);
  const shownKey = `announcements-shown:${sessionToken || 'anon'}`;

  useEffect(() => {
    if (!active || !sessionToken || !employee || onFillRoute) return;
    if (sessionStorage.getItem(shownKey)) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/announcements/unseen`, { headers: getAuthHeader() });
        if (!r.ok || cancelled) return;
        const d = await r.json();
        if (Array.isArray(d) && d.length > 0) {
          setItems(d);
          setOpen(true);
          sessionStorage.setItem(shownKey, '1');
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, sessionToken, employee?.employee_id, onFillRoute]);

  const openOne = (a) => {
    setOpen(false);
    navigate(`/announcements?open=${a.announcement_id}`);
  };

  const viewAll = () => { setOpen(false); navigate('/announcements'); };

  if (!open || onFillRoute) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-3xl p-0 overflow-hidden gap-0" data-testid="announcement-popup">
        <DialogTitle className="sr-only">New announcements</DialogTitle>
        <div className="grid md:grid-cols-5">
          {/* Left visual panel (matches survey popup) */}
          <div className="md:col-span-2 relative hidden md:flex items-center justify-center p-6"
            style={{ background: `linear-gradient(160deg, ${ACCENT}14, ${ACCENT}33)` }}>
            <motion.div
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 120, damping: 14 }}
            >
              <Megaphone className="w-28 h-28 drop-shadow-xl" style={{ color: ACCENT }} data-testid="popup-icon" />
            </motion.div>
            <div className="absolute top-4 left-4 flex items-center gap-2 text-xs font-bold tracking-wide" style={{ color: ACCENT }}>
              <Megaphone className="h-4 w-4" /> ANNOUNCEMENTS
            </div>
          </div>

          {/* Right content panel */}
          <div className="md:col-span-3 p-7 space-y-4">
            <div>
              <p className="text-xs font-bold tracking-[0.1em] uppercase text-muted-foreground">What's new</p>
              <h2 className="text-2xl font-bold tracking-tight leading-snug mt-1">
                You have {items.length} new announcement{items.length === 1 ? '' : 's'}
              </h2>
            </div>

            <div className="max-h-[40vh] overflow-y-auto -mx-2 divide-y">
              {items.map(a => (
                <motion.button
                  key={a.announcement_id}
                  type="button"
                  whileHover={{ x: 3 }}
                  onClick={() => openOne(a)}
                  className="w-full text-left px-2 py-2.5 flex items-center gap-3 hover:bg-accent/50 transition-colors rounded-md"
                  data-testid={`popup-item-${a.announcement_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className={`text-[10px] ${CATEGORY_STYLES[a.category]}`}>{a.category}</Badge>
                      <Badge className="bg-red-600 text-white hover:bg-red-600 text-[10px]">NEW</Badge>
                      <span className="text-xs text-muted-foreground">{fmtDate(a.publish_date)}</span>
                    </div>
                    <p className="font-medium text-sm mt-1 truncate">{a.title}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                </motion.button>
              ))}
            </div>

            <div className="flex items-center justify-end gap-2 pt-1">
              <motion.div whileTap={{ scale: 0.97 }}>
                <Button variant="ghost" onClick={() => setOpen(false)} data-testid="popup-close-btn">Close</Button>
              </motion.div>
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                <Button onClick={viewAll} data-testid="popup-viewall-btn" className="text-white shadow-lg" style={{ backgroundColor: ACCENT }}>
                  View all <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
              </motion.div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
